/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IContentBlockAdditionalContentItemViewProps,
    IContentBlockAdditionalContentViewProps,
    IContentBlockProps,
    IContentBlockViewProps
} from '@msdyn365-commerce-modules/content-block';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IContentBlockData } from '../../../dataTypes/content-block.data';

type ElementWithLinks = {
    links?: { linkText?: string }[];
};

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IContentBlockAdditionalContentViewProps) => {
    return (
        <Node {...additionalContent.additionalContentNode}>
            {additionalContent.additionalContentItems?.map((item: IContentBlockAdditionalContentItemViewProps) => {
                return (
                    <>
                        {item.heading}
                        <Node {...item.additionalContentItemContainer}>
                            {item.text}
                            <Node {...item.additionalContentItemLinks}>{item.links}</Node>
                        </Node>
                    </>
                );
            })}
        </Node>
    );
};

/**
 * Render View.
 * @param props - The view props.
 * @returns -The JSX Element.
 */
const contentBlockView: React.FC<IContentBlockViewProps & IContentBlockProps<IContentBlockData>> = props => {
    const {
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        title,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        imageAriaLabel,
        additionalContent
    } = props;

    // @ts-expect-error HTML element need to be clear on run time.
    const hasImage: boolean = image.props.src;
    const imageClass: string = hasImage ? `${detailsContainer.className} withImage` : `${detailsContainer.className} withoutImage`;
    const isCheckoutButtonEnabled = props.data.getCheckoutButtonState?.result?.isEnable ?? false;

    const updateElement = <T extends ElementWithLinks>(element: React.ReactNode, cloneProps: T): React.ReactNode => {
        if (React.isValidElement<T>(element)) {
            const node = element.props;
            if (node?.links?.[0]?.linkText === 'Checkout') {
                if (isCheckoutButtonEnabled) {
                    return React.cloneElement(element, cloneProps);
                }

                return (
                    <div className='ms-content-block__cta'>
                        <button
                            type='button'
                            className='msc-cta__primary'
                            title='Checkout'
                            aria-label='Checkout'
                            disabled={true}
                            style={{ background: '#c4c4c4', color: 'white', border: '1px solid #c4c4c4', cursor: 'not-allowed' }}
                        >
                            Checkout
                        </button>
                    </div>
                );
            }
            return React.cloneElement(element, cloneProps);
        }
        return element;
    };
    const updatedLinks = updateElement(links, {});

    if (imageLink) {
        return (
            <Module {...contentBlockContainer}>
                <Node
                    {...contentBlockAnchorTag}
                    href={imageLink}
                    className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                    aria-label={imageAriaLabel}
                >
                    <Node {...imageContainer}>{image}</Node>
                </Node>
                <Node {...detailsContainer}>
                    {title}
                    {text}
                    {updatedLinks}
                    {additionalContent && renderAdditionalContent(additionalContent)}
                </Node>
            </Module>
        );
    }
    return (
        <Module {...contentBlockContainer}>
            <Node {...imageContainer}>{image}</Node>
            <Node className={imageClass}>
                {title}
                {text}
                {updatedLinks}
                {additionalContent && renderAdditionalContent(additionalContent)}
            </Node>
        </Module>
    );
};

export default contentBlockView;
